import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Task } from "../../types/Work";

const DAY_MS = 24 * 60 * 60 * 1000;
const CHART_RANGES = [3, 7, 30, 90] as const;

interface WorkStatsSectionProps {
  tasks: Task[];
}

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const dateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const formatMinutes = (minutes: number) => {
  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  return `${mins}m`;
};

export const WorkStatsSection: React.FC<WorkStatsSectionProps> = ({ tasks }) => {
  const [chartDays, setChartDays] = useState<(typeof CHART_RANGES)[number]>(7);

  const stats = useMemo(() => {
    const today = startOfDay(new Date());
    const minutesByDay = new Map<string, number>();

    tasks.forEach((task) => {
      task.timeEntries?.forEach((entry) => {
        const key = dateKey(new Date(entry.startAt));
        minutesByDay.set(
          key,
          (minutesByDay.get(key) ?? 0) + (entry.durationMinutes || 0),
        );
      });
    });

    const lastSevenValues = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(today.getTime() - (6 - index) * DAY_MS);
      return minutesByDay.get(dateKey(date)) ?? 0;
    });
    const activeValues = lastSevenValues.filter((minutes) => minutes > 0);

    const thisMondayOffset = (today.getDay() + 6) % 7;
    const thisMonday = new Date(today.getTime() - thisMondayOffset * DAY_MS);
    const lastMonday = new Date(thisMonday.getTime() - 7 * DAY_MS);
    const lastWeekMinutes = Array.from({ length: 5 }, (_, index) => {
      const date = new Date(lastMonday.getTime() + index * DAY_MS);
      return minutesByDay.get(dateKey(date)) ?? 0;
    }).reduce((sum, minutes) => sum + minutes, 0);

    const sevenDaysAgo = today.getTime() - 6 * DAY_MS;
    const completedLastSevenDays = tasks.filter((task) => {
      if (task.status !== "done") return false;
      const completed = startOfDay(task.completedAt ?? task.updatedAt).getTime();
      return completed >= sevenDaysAgo && completed <= today.getTime();
    }).length;

    const chartData = Array.from({ length: chartDays }, (_, index) => {
      const date = new Date(today.getTime() - (chartDays - 1 - index) * DAY_MS);
      const minutes = minutesByDay.get(dateKey(date)) ?? 0;
      return {
        date: date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
        minutes,
        hours: Number((minutes / 60).toFixed(2)),
      };
    });
    const rangeMinutes = chartData.reduce((sum, day) => sum + day.minutes, 0);
    const rangeActiveDays = chartData.filter((day) => day.minutes > 0).length;

    return {
      todayMinutes: minutesByDay.get(dateKey(today)) ?? 0,
      activeDayAverage: activeValues.length
        ? activeValues.reduce((sum, minutes) => sum + minutes, 0) /
          activeValues.length
        : 0,
      lastWeekAverage: lastWeekMinutes / 5,
      ticketsPerDay: completedLastSevenDays / 7,
      rangeMinutes,
      rangeActiveDays,
      chartData,
    };
  }, [tasks, chartDays]);

  return (
    <section className="work-stats-section">
      <div className="work-stats-header">
        <div>
          <h3><i className="fas fa-chart-column"></i> Work Stats</h3>
          <p>Your recent time and completion trends</p>
        </div>
        <div className="chart-range-buttons" aria-label="Chart date range">
          {CHART_RANGES.map((days) => (
            <button
              type="button"
              key={days}
              className={chartDays === days ? "active" : ""}
              aria-pressed={chartDays === days}
              onClick={() => setChartDays(days)}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      <div className="work-stat-cards">
        <div className="work-stat-card"><span><i className="fas fa-clock"></i> Today</span><strong>{formatMinutes(stats.todayMinutes)}</strong><small>Time logged today</small></div>
        <div className="work-stat-card"><span><i className="fas fa-chart-line"></i> Avg. active day</span><strong>{formatMinutes(stats.activeDayAverage)}</strong><small>Last 7 days, logged days only</small></div>
        <div className="work-stat-card"><span><i className="fas fa-calendar-week"></i> Avg. weekday</span><strong>{formatMinutes(stats.lastWeekAverage)}</strong><small>Last week, Monday-Friday</small></div>
        <div className="work-stat-card"><span><i className="fas fa-circle-check"></i> Tickets closed/day</span><strong>{stats.ticketsPerDay.toFixed(1)}</strong><small>Last 7 days</small></div>
      </div>

      <div className="work-hours-chart" aria-label={`Hours worked over the last ${chartDays} days`}>
        <div className="work-chart-summary">
          <div><span>Time logged · last {chartDays} days</span><strong>{formatMinutes(stats.rangeMinutes)}</strong></div>
          <span className="work-chart-active-days">{stats.rangeActiveDays} active {stats.rangeActiveDays === 1 ? "day" : "days"}</span>
        </div>
        {stats.rangeMinutes === 0 ? (
          <div className="work-chart-empty">
            <i className="fas fa-chart-simple"></i>
            <span>No time logged in this range</span>
          </div>
        ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={stats.chartData} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
            <XAxis dataKey="date" stroke="#a1a1aa" tickLine={false} axisLine={false} minTickGap={18} />
            <YAxis stroke="#a1a1aa" tickLine={false} axisLine={false} unit="h" />
            <Tooltip
              formatter={(value: number | undefined) => [formatMinutes(Math.round((value ?? 0) * 60)), "Time logged"]}
              cursor={{ fill: "rgba(48, 206, 212, 0.07)" }}
              contentStyle={{
                background: "#242426",
                border: "1px solid rgba(48, 206, 212, 0.25)",
                borderRadius: 12,
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
              }}
              labelStyle={{ color: "#f5f5f7", fontWeight: 700 }}
              itemStyle={{ color: "#30ced4" }}
            />
            <Bar dataKey="hours" fill="#30ced4" radius={[6, 6, 0, 0]} maxBarSize={48} />
          </BarChart>
        </ResponsiveContainer>
        )}
      </div>
    </section>
  );
};
